import type { LinkPreviewContextResponse } from '@sweetistics/contracts';
import type { Dispatch, SetStateAction } from 'react';
import type { LinkMetadata as FetchedLinkMetadata } from '../../../lib/fetch-metadata';
import type { SummaryLengthOption } from '../../../lib/link-preview/summary-options';
import type { TranscriptSource } from '../../../lib/link-preview/types';
import type { AICostSummary, AIUsageSummary } from '../../../types/ai';

export type { TranscriptSource } from '../../../lib/link-preview/types';

export type LinkMetadata = FetchedLinkMetadata;

/**
 * Chat context payload for link previews extends the base contract response with
 * lightweight metadata so future prompts can reference the captured snapshot.
 */
export interface LinkPreviewContextData extends LinkPreviewContextResponse {
  previewMetadata?: {
    title: string | null;
    description: string | null;
    siteName: string | null;
    image: string | null;
  } | null;
  snapshotCollectedAt?: string;
}

export type SummaryStatus = 'idle' | 'loading' | 'streaming' | 'complete' | 'error';
export type SummaryModeSetting = 'summary' | 'explain' | 'custom';
export type SummarySearchMode = 'auto' | 'off';

export interface SummaryMetadata {
  availableImages: number | null;
  consideredTweets: number | null;
  contentBudgetCharacters: number | null;
  contentTruncated: boolean | null;
  cost: AICostSummary | null;
  customPrompt: string | null;
  finishReason: string | null;
  includedImages: number | null;
  includedLinks: number | null;
  mode: SummaryModeSetting | null;
  model: string | null;
  requestedModel: string | null;
  requestedSearchMode: SummarySearchMode | null;
  resolvedModel: string | null;
  responseTimeMs: number | null;
  searchMode: SummarySearchMode | null;
  shouldIncludeImages: boolean | null;
  sourceCharacters: number | null;
  statusMessage: string | null;
  summaryCharacters: number | null;
  summaryLength: SummaryLengthOption | null;
  tokenHint: number | null;
  totalTokens: number | null;
  transcriptAvailable: boolean | null;
  transcriptCharacters: number | null;
  transcriptLines: number | null;
  transcriptMessage: string | null;
  transcriptSource: TranscriptSource | null;
  truncated: boolean | null;
  usage: AIUsageSummary | null;
  videoContentType: string | null;
  // Video summarizer metadata (populated when Gemini analyses a clip).
  videoDurationSeconds: number | null;
  videoPipelinePhase: string | null;
  videoPosterUrl: string | null;
  videoSizeBytes: number | null;
  videoSource: string | null;
  videoTitle: string | null;
  wordCount: number | null;
}

export interface SummaryState {
  error: string | null;
  metadata: SummaryMetadata;
  status: SummaryStatus;
  text: string;
}

export const DEFAULT_SUMMARY_METADATA: SummaryMetadata = {
  requestedModel: null,
  resolvedModel: null,
  tokenHint: null,
  includedImages: null,
  availableImages: null,
  shouldIncludeImages: null,
  customPrompt: null,
  mode: null,
  searchMode: null,
  requestedSearchMode: null,
  finishReason: null,
  totalTokens: null,
  responseTimeMs: null,
  sourceCharacters: null,
  summaryCharacters: null,
  wordCount: null,
  truncated: null,
  contentTruncated: null,
  contentBudgetCharacters: null,
  summaryLength: null,
  model: null,
  transcriptCharacters: null,
  transcriptLines: null,
  transcriptAvailable: null,
  transcriptMessage: null,
  transcriptSource: null,
  usage: null,
  cost: null,
  consideredTweets: null,
  includedLinks: null,
  // Default video metadata mirrors the structure above so selectors can rely on null semantics.
  videoDurationSeconds: null,
  videoSizeBytes: null,
  videoContentType: null,
  videoSource: null,
  videoTitle: null,
  videoPosterUrl: null,
  videoPipelinePhase: null,
  statusMessage: null,
};

export type SummaryStateSetter = Dispatch<SetStateAction<SummaryState>>;
